import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { atom, useSetAtom } from 'jotai';

import { useWorkersService, CvParsingJobStatus } from '@restworld/data-services';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useFetchWorkerDetailsQueryKey } from './use-load-worker-data';

const cvParsingJobStatusQueryKey = () => [QUERY_KEYS.WORKERS.CV_PARSING_JOB_STATUS];

const cvParsingLoaderAtom = atom<boolean>(false);

export const useCvParsingJobStatus = () => {
  const workerService = useWorkersService();
  const queryClient = useQueryClient();
  const setLoader = useSetAtom(cvParsingLoaderAtom);
  const [enabled, setEnabled] = useState(true);
  const workerQueryKey = useFetchWorkerDetailsQueryKey();

  const queryResult = useQuery<CvParsingJobStatus | null>({
    queryKey: cvParsingJobStatusQueryKey(),
    queryFn: async () => {
      const response = await workerService.fetchCvParsingJobStatus();
      return response?.data ?? null;
    },
    enabled,
    refetchInterval: enabled ? 2000 : false,
    retry: (failureCount, error) => {
      const err = error as { code?: number };
      if (err && typeof err.code === 'number' && [400, 401, 404].includes(err.code)) return false;
      return failureCount < 3;
    },
  });

  const { data, isLoading, isFetching } = queryResult;

  useEffect(() => {
    if (!data) return;
    const isCompleted = data.status === 'not_found' || data.state === 'completed' || data.status === 'completed';
    if (isCompleted && enabled) {
      setEnabled(false);
      queryClient.invalidateQueries({ queryKey: workerQueryKey });
    }
  }, [data, enabled, queryClient, workerQueryKey]);

  useEffect(() => {
    const isJobOngoing = !!data && data.status !== 'not_found' && data.state !== null && data.state !== 'completed';
    const loading = enabled && (isLoading || isFetching || isJobOngoing);
    setLoader(loading);
  }, [data, enabled, isFetching, isLoading, setLoader]);

  return queryResult;
};

export const cvParsingJobStatusQueryKeyFactory = cvParsingJobStatusQueryKey;
export { cvParsingLoaderAtom };
